import { Alert, Button, Loader } from '@mantine/core';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useMyFundCallLines } from 'src/Hooks/Queries/useFundCalls';
import { trackMyStatementViewed } from 'src/Services/Mixpanel/events';
import { asFundCallLineDetail } from 'src/types/fund-call.types';
import { groupIban } from 'src/Utils/formatIban';

import styles from './MyStatement.module.scss';

/** « Mon relevé » : ce que je dois, pourquoi, à qui, et comment le régler. */
const MyStatement = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: fundCalls, isLoading, isError } = useMyFundCallLines();

  const pendingCount = (fundCalls ?? []).reduce((sum, call) => sum + call.lines.length, 0);

  useEffect(() => {
    if (!isLoading) trackMyStatementViewed({ pending_lines: pendingCount });
  }, [isLoading, pendingCount]);

  if (isLoading) return <Loader />;

  // Même règle que la liste : « vous n'avez rien à verser » serait un mensonge sur un échec.
  if (isError) {
    return (
      <Alert color="red" title={t('fundCalls.myStatement.errorTitle', 'Chargement impossible')}>
        {t(
          'fundCalls.myStatement.errorBody',
          'Votre relevé n’a pas pu être chargé. Réessayez dans un instant.'
        )}
      </Alert>
    );
  }

  if (!fundCalls || fundCalls.length === 0) {
    return (
      <p className={styles.empty}>
        {t('fundCalls.myStatement.empty', 'Vous n’avez rien à verser. Tout est à jour.')}
      </p>
    );
  }

  return (
    <div className={styles.container}>
      {fundCalls.map(fundCall =>
        fundCall.lines.map(line => {
          const detail = asFundCallLineDetail(line.detail);
          const remaining = line.amountCalled - line.amountPaid;

          return (
            <div key={line.id} className={styles.card}>
              <div className={styles.title}>{fundCall.title}</div>
              <div className={styles.amount}>
                {t('fundCalls.myStatement.toPay', '{{amount}} € à verser', {
                  amount: remaining.toFixed(2),
                })}
              </div>
              {line.amountPaid > 0 && remaining > 0 && (
                <div className={styles.meta}>
                  {t('fundCalls.myStatement.alreadyPaid', '{{amount}} € déjà versés', {
                    amount: line.amountPaid.toFixed(2),
                  })}
                </div>
              )}
              {fundCall.dueDate && (
                <div className={styles.meta}>
                  {t('fundCalls.myStatement.before', 'avant le {{date}}', {
                    date: dayjs(fundCall.dueDate).format('DD/MM/YYYY'),
                  })}
                </div>
              )}

              {detail.creditorGroups.map(group => (
                <div key={group.creditorLabel}>
                  <div className={styles.groupTitle}>
                    {group.creditorLabel} — {group.total.toFixed(2)} €
                  </div>
                  {group.rows.map((row, index) => (
                    <div key={`${group.creditorLabel}-${index}`} className={styles.detailRow}>
                      <span>{[row.date, row.label].filter(Boolean).join(' · ')}</span>
                      <span>{row.share.toFixed(2)} €</span>
                    </div>
                  ))}
                </div>
              ))}

              {/* `bankDetail` is the branch (« Île-de-France »), not where to pay: showing it
                  here was the « Pour régler : IDF » of the 14/09 test. */}
              <div className={styles.groupTitle}>
                {t('fundCalls.myStatement.howToPay', 'Pour régler')}
              </div>
              {fundCall.bankAccount?.iban ? (
                <>
                  <div className={styles.meta}>
                    {fundCall.bankAccount.holderName ?? fundCall.bankAccount.name}
                  </div>
                  <div className={styles.meta}>
                    {t('fundCalls.myStatement.iban', 'IBAN {{iban}}', {
                      iban: groupIban(fundCall.bankAccount.iban),
                    })}
                  </div>
                  {fundCall.bankAccount.bic && (
                    <div className={styles.meta}>
                      {t('fundCalls.myStatement.bic', 'BIC {{bic}}', {
                        bic: fundCall.bankAccount.bic,
                      })}
                    </div>
                  )}
                </>
              ) : (
                <div className={styles.meta}>
                  {t(
                    'fundCalls.myStatement.noCoordinates',
                    'Le gestionnaire vous indiquera comment régler.'
                  )}
                </div>
              )}
              {line.transferReference && (
                <div className={styles.meta}>
                  {t('fundCalls.myStatement.reference', 'Référence à indiquer : {{reference}}', {
                    reference: line.transferReference,
                  })}
                </div>
              )}

              <div className={styles.actions}>
                <Button
                  onClick={() =>
                    navigate('../new-transfer', {
                      state: { amount: remaining, fromUserId: line.party.id },
                    })
                  }
                >
                  {t('fundCalls.myStatement.iPaid', 'J’ai payé')}
                </Button>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

export default MyStatement;
